import React, { createRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { defaultComponents } from "../../../controls/Material-Table-Components";
import { TablePagination, Checkbox, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import CommoditiesValidator from "../validators/CommoditiesValidator";
import {
  useCommodities,
  useAddCommodity,
  useUpdateCommodity,
  useDeleteCommodity,
} from "../../../services/settings";
import {MaskedText, MaskedNumber} from "../../../controls";


const CommodityList = (_props) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth0();
  const validatorColumn = ValidatorColumn(CommoditiesValidator, true);

  const props = {
    ..._props,
  };
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null,
  });

  const tableRef = createRef({ state: { lastEditingRow: false } });

  const handleAddCountry = useAddCommodity();
  const handleUpdateCountry = useUpdateCommodity();
  const handleDeleteCountry = useDeleteCommodity();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const commodities = useCommodities({ tableRef, queryClient, extraKeys });

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize: 14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars = {
      U: "[A-Z]",
      A: "[A-Z0-9]",
      a: "[A-Za-z0-9]",
      i: "[A-Za-z0-9\\-]",
      9: "[0-9]",
      D: "[0-9.]",
      "-": "-",
      x: ".",
    };
    const defaultEditProps = {
      style: { fontSize: "0.875rem" },
    };

    return [
      { ...validatorColumn },
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        title: "Vertical",
        field: "verticalName",
        initialEditValue: "",
        editable: "never",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Commodity Code",
        field: "commodityCode",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(8)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.commodityCode}
            decimalScale={0}
            thousandSeparator=""
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(20),
      },
      {
        ...baseColumn,
        title: "Commodity Name",
        field: "commodityName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Active",
        field: "active",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: (rowData) => (
          <Checkbox
            style={{ padding: 0 }}
            checked={rowData.active}
            value="true"
          />
        ),
      },
    ];
  };

  return (
    isAuthenticated && (
      <div style={{ width: "100%", marginLeft: 20, marginRight: 20 }}>
        <MaterialTable
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "8px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            toolbarButtonAlignment: "right",
            pageSize: 10,
            detailPanelType: "single",
            // paging: false,
            // overflowY: "scroll",
            // minBodyHeight: "calc(100vh - 230px)", //168+60//FIXME to calculate dynamic height, needed for correct scroll position identification
            // maxBodyHeight: "calc(100vh - 230px)"
          }}
          components={{
            ...defaultComponents,
            Pagination: (props) => (
              <TablePagination {...props} rowsPerPageOptions={[5, 10, 15]} />
            ),
          }}
          title="Commodities"
          columns={getColumns({ canEdit: state.canEdit })}
          data={commodities.data}
          editable={{
            isEditable: () => {
              return state.canEdit;
            },
            isDeletable: () => {
              return state.canEdit;
            },
            onRowAdd: state.canEdit
              ? (newData) =>
                  new Promise((resolve, reject) => {
                    handleAddCountry.mutate({
                      record: newData,
                      resolve,
                      reject,
                      extraKeys,
                    });
                  })
              : undefined,
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                handleUpdateCountry.mutate({
                  record: newData,
                  oldRecord: oldData,
                  resolve,
                  reject,
                  extraKeys,
                });
              }),
            onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteCountry.mutate({
                  oldRecord: oldData,
                  resolve,
                  reject,
                  extraKeys,
                });
              }),
          }}
        />
      </div>
    )
  );
};

export default CommodityList;
